import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon } from 'lucide-react';
import { ChristmasSectionHeading } from './ChristmasSectionHeading';
import { GoogleLogo } from './GoogleLogo';
import { ReviewCard } from './ReviewCard';
import { SampleReviewCard } from './SampleReviewCard';
import { useReviewCarousel } from './useReviewCarousel';
import { googleProfileUrl, christmasReviews, sampleReviews, MAX_REVIEWS } from '../../../data/christmasReviews';
import type { ChristmasReview, SampleReview } from '../../../data/christmasReviews';

// Genuine reviews when available; otherwise clearly-labeled sample reviews for preview.
type Slide = {
  key: string;
  review?: ChristmasReview;
  sample?: SampleReview;
};
const genuine = christmasReviews.slice(0, MAX_REVIEWS);
const slides: Slide[] = genuine.length ? genuine.map(r => ({
  key: r.id,
  review: r
})) : sampleReviews.slice(0, MAX_REVIEWS).map(s => ({
  key: s.id,
  sample: s
}));
const arrowBtn = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-forest ring-1 ring-forest/10 shadow-sm transition-colors hover:bg-olive hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage';
export function ChristmasTestimonialsSection() {
  const {
    index,
    direction,
    next,
    prev,
    goTo,
    setPaused
  } = useReviewCarousel(slides.length, 5000);
  const slide = slides[index];
  const multiple = slides.length > 1;
  return <section id="christmas-testimonials" aria-labelledby="christmas-testimonials-title" className="scroll-mt-32 bg-white py-20 md:py-28" data-id="element-243" data-mp="f0fq8lv80kcy">
      <div className="mx-auto max-w-4xl px-5 sm:px-8" data-id="element-244" data-mp="a8o88zq4rmyf">
        <ChristmasSectionHeading eyebrow="Customer Reviews" title="Christmas Light Installation Reviews" description="See what our customers say about their holiday lighting experience." titleId="christmas-testimonials-title" data-id="element-245" data-mp="5bo0mkdmuc57" />

        <div className="mt-12" role="region" aria-roledescription="carousel" aria-label="Christmas light installation reviews" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} onKeyDown={e => {
        if (e.key === 'ArrowRight') next();
        if (e.key === 'ArrowLeft') prev();
      }} data-id="element-246" data-mp="1gw27s042k785">
          <div className="flex items-center gap-3 sm:gap-5" data-id="element-247" data-mp="y7x7x3qj96em">
            {multiple && <button type="button" onClick={prev} aria-label="Previous review" className={`${arrowBtn} hidden sm:flex`} data-id="element-248" data-mp="132kqklyuv6la">
                <ChevronLeftIcon className="h-5 w-5" data-id="element-249" data-mp="t67vbjnnwkof" />
              </button>}

            <div className="relative min-h-[340px] flex-1 overflow-hidden" data-id="element-250" data-mp="2jixbkiyr9mpt">
              <AnimatePresence mode="wait" initial={false} data-id="element-251" data-mp="2jgsybaeyvbcv">
                <motion.div key={slide.key} initial={{
                opacity: 0,
                x: direction * 40
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: direction * -40
              }} transition={{
                duration: 0.45,
                ease: [0.22, 1, 0.36, 1]
              }} role="group" aria-roledescription="slide" aria-label={`Review ${index + 1} of ${slides.length}`} className="h-full" data-id="element-252" data-mp="2njha9qyz8ye2">
                  {slide.sample ? <SampleReviewCard review={slide.sample} data-id="element-253" data-mp="vjbh6sqcn7w8" /> : <ReviewCard review={slide.review} data-id="element-254" data-mp="qmkyryr2o07n" />}
                </motion.div>
              </AnimatePresence>
            </div>

            {multiple && <button type="button" onClick={next} aria-label="Next review" className={`${arrowBtn} hidden sm:flex`} data-id="element-255" data-mp="14jqbuip7dzce">
                <ChevronRightIcon className="h-5 w-5" data-id="element-256" data-mp="36d7jvnh8x27o" />
              </button>}
          </div>

          {multiple && <div className="mt-6 flex items-center justify-center gap-4" data-id="element-257" data-mp="31f60f1fwr0vo">
              <button type="button" onClick={prev} aria-label="Previous review" className={`${arrowBtn} sm:hidden`} data-id="element-258" data-mp="33t1d7818yxnf">
                <ChevronLeftIcon className="h-5 w-5" data-id="element-259" data-mp="37tlaqcpqg3a3" />
              </button>
              <div className="flex max-w-[260px] flex-wrap items-center justify-center gap-1.5" data-id="element-260" data-mp="3ctpi9d8nxno6">
                {slides.map((s, i) => <button key={s.key} type="button" onClick={() => goTo(i)} aria-label={`Show review ${i + 1}`} aria-current={i === index ? 'true' : undefined} className={`h-2 rounded-full transition-all ${i === index ? 'w-6 bg-olive' : 'w-2 bg-forest/20 hover:bg-forest/40'}`} data-id="element-261" data-mp="3hr7v3epswn1g" data-mpi={i} />)}
              </div>
              <button type="button" onClick={next} aria-label="Next review" className={`${arrowBtn} sm:hidden`} data-id="element-262" data-mp="21ekdm3oz1f1x">
                <ChevronRightIcon className="h-5 w-5" data-id="element-263" data-mp="26cu7mayym9ly" />
              </button>
            </div>}
        </div>

        {googleProfileUrl && <div className="mt-8 text-center" data-id="element-264" data-mp="1yt34brr9k8s5">
            <a href={googleProfileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-olive underline-offset-4 hover:text-forest hover:underline" data-id="element-265" data-mp="1tu8ggbefvu16">
              <GoogleLogo className="h-4 w-4" data-id="element-266" data-mp="1fv0exbut09tt" /> Read More Reviews on Google
              <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-267" data-mp="1fx3d4j64199m" />
            </a>
          </div>}
      </div>
    </section>;
}